import type { GoogleWorkspacePermissionState } from '@oracord/shared';

export type StaffPrivateCoachingShellStatus = 'matched' | 'setup_needed' | 'loading';

export interface StaffPrivateCoachingShellCard {
  id: 'destination' | 'mobile' | 'manager-boundary';
  title: string;
  detail: string;
  badge: string;
  tone: 'slate' | 'emerald' | 'amber' | 'purple';
}

export interface StaffPrivateCoachingShellSummary {
  status: StaffPrivateCoachingShellStatus;
  title: string;
  detail: string;
  cards: StaffPrivateCoachingShellCard[];
}

export function buildStaffPrivateCoachingShellSummary(input: {
  matched: boolean;
  profileDisplayName?: string | null;
  hasBusinessNumber?: boolean;
  hasMobileSession?: boolean;
  googlePermissionState?: GoogleWorkspacePermissionState;
}): StaffPrivateCoachingShellSummary {
  if (!input.matched) {
    return {
      status: 'setup_needed',
      title: 'Connect your staff profile first',
      detail:
        'Private coaching is tied to your own matched Team profile. Finish staff setup before Oracord can show guidance meant only for you.',
      cards: [
        {
          id: 'destination',
          title: 'Your private coaching space',
          detail:
            'This page is reserved for coaching that belongs to the signed-in staff member. It does not show manager notes or team comparisons.',
          badge: 'Setup needed',
          tone: 'amber',
        },
        {
          id: 'manager-boundary',
          title: 'Managers see the big picture',
          detail:
            'Manager views stay limited to aggregate health and themes. They do not show your private coaching items or compare staff against each other.',
          badge: 'Private by default',
          tone: 'purple',
        },
      ],
    };
  }

  const profileName = input.profileDisplayName?.trim() || 'your staff profile';
  const hasBusinessNumber = input.hasBusinessNumber === true;
  const hasMobileSession = input.hasMobileSession === true;
  const googleConnected = input.googlePermissionState === 'connected';

  return {
    status: 'matched',
    title: `Private coaching for ${profileName}`,
    detail:
      'This is your staff-facing coaching destination. Coaching items appear here only after Oracord creates staff-private events from supported communication sources.',
    cards: [
      {
        id: 'destination',
        title: 'No private coaching items yet',
        detail:
          'There are no coaching cards to show right now. Oracord will not invent tips, recommendations, ratings, or customer stories just to fill this space.',
        badge: 'Empty for now',
        tone: 'slate',
      },
      {
        id: 'mobile',
        title: hasMobileSession ? 'Mobile access is linked' : hasBusinessNumber ? 'Prepare mobile access when ready' : 'Keep setup moving',
        detail: hasMobileSession
          ? 'Mobile app access is linked to your staff profile. Keep your assigned number and work-data permissions current so future private coaching can reach the right place.'
          : hasBusinessNumber
            ? 'Your business number is assigned. Prepare mobile access from this page so private coaching can be available to your staff-owned app experience when real items exist.'
            : 'Ask an admin to assign your business number, then prepare mobile access when it is ready. Until then, this page stays a private empty destination.',
        badge: hasMobileSession ? 'Mobile linked' : hasBusinessNumber ? 'Ready for mobile' : 'Setup needed',
        tone: hasMobileSession ? 'emerald' : hasBusinessNumber ? 'amber' : 'slate',
      },
      {
        id: 'manager-boundary',
        title: 'Private to staff, aggregate for managers',
        detail: googleConnected
          ? 'Your Google work-data permission is connected. Manager reporting still stays aggregate-only and does not expose private coaching text.'
          : 'Manager reporting stays aggregate-only. Connecting work-data permissions later does not create manager drill-through into your private coaching items.',
        badge: 'Privacy boundary',
        tone: 'purple',
      },
    ],
  };
}
